import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = article ? getRelatedArticles(article) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-screen">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-warning-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 mb-3">内容未找到</h1>
            <p className="text-foreground-400 mb-6">抱歉，您访问的内容不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - 91制片厂原创影视作品`,
    description: article.summary,
    image: article.coverImage,
    datePublished: article.releaseDate,
    author: {
      '@type': 'Person',
      name: article.director,
    },
    publisher: {
      '@type': 'Organization',
      name: '91制片厂',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.91pianzhi.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />

          <article className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium text-primary-400 bg-primary-500/10 rounded-full whitespace-nowrap hover:bg-primary-500/20 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs text-foreground-400 bg-background-200/50 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
                {article.featured && (
                  <span className="px-3 py-1 text-xs font-medium text-background-950 bg-accent-500 rounded-full whitespace-nowrap">
                    精选推荐
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-400 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  <strong className="text-foreground-200 font-medium">导演：</strong>{article.director}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-group-line"></i>
                  <strong className="text-foreground-200 font-medium">主演：</strong>{article.cast.join('、')}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.releaseDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.duration}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-4k-line"></i>
                  {article.quality}
                </span>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-accent-400">
                  <i className="ri-star-fill"></i>
                  <strong>{article.rating}</strong>
                </span>
                <span className="flex items-center gap-1 text-foreground-400">
                  <i className="ri-eye-line"></i>
                  {(article.views / 10000).toFixed(0)}万次观看
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10 bg-background-100">
              <img
                src={article.coverImage}
                alt={`${article.title} - 91制片厂原创影视作品海报`}
                title={`${article.title} - 91制片厂${article.categoryName}`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-invert max-w-none text-foreground-200 leading-relaxed text-sm md:text-base space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/50">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-400">分享：</span>
                <button className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full text-foreground-400 hover:text-primary-400 hover:bg-primary-500/10 transition-colors cursor-pointer">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full text-foreground-400 hover:text-primary-400 hover:bg-primary-500/10 transition-colors cursor-pointer">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full text-foreground-400 hover:text-primary-400 hover:bg-primary-500/10 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </button>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto mt-14 md:mt-20">
              <div className="mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                  相关<span className="text-primary-400">推荐</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} size="small" />
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="py-12"></div>
      </main>
      <Footer />
    </>
  );
}